import { productConfigUrl } from "@husky-hrc/Framework/Services/ProductConfigServiceV1";
import {
    BomDto,
    ElementDto,
    GroupModel,
    ImageInputModel,
    ImageValueModel,
    InputModel,
    InputRowModel,
    InputType,
    MemberDto,
    PropertiesDto,
    StepDto,
    ValueModel,
} from "@husky-hrc/Framework/Types/ApiModel";
import { propertyTruthy } from "@husky-hrc/Utilities/Utilities";
import groupBy from "lodash/groupBy";

let translationGroup: MemberDto | undefined;

export const parseSteps = (step: StepDto) => {
    translationGroup = traverseForValue(step.rootGroup?.members, "name", "css_support");
    const groupsToTraverse = step.rootGroup?.members?.reduce((result: MemberDto[], member: MemberDto) => {
        if (!propertyTruthy(member.properties?.css_hideTree)) {
            return result.concat(parseGroups(member));
        }
        return result;
    }, []);
    const inputs = getInputsForStep(groupsToTraverse);
    const inputsByGroupName = groupBy(inputs, input => input.groupName);
    return getGroupModelsFromInputs(inputsByGroupName);
};

const getInputsForStep = (groups: MemberDto[]) => {
    let inputs: Array<InputModel | ImageInputModel> = [];
    groups?.forEach(group => {
        inputs = inputs.concat(parseInputs(group));
    });
    return inputs;
};

const getGroupModelsFromInputs = (inputsByGroupName: Record<string, Array<InputModel | ImageInputModel>>) => {
    const groups: GroupModel[] = [];
    Object.keys(inputsByGroupName).forEach(group => {
        const groupInputs = inputsByGroupName[group];
        const rows = groupInputsByRow(groupInputs);
        groups.push({
            name: group,
            description: group,
            rows,
        });
    });
    return groups;
};

const parseGroups = (group: MemberDto, groups: MemberDto[] = []) => {
    if (!propertyTruthy(group.properties?.css_hide) && !propertyTruthy(group.properties?.hidden)) {
        groups.push(group);
    } else {
        group.members
            .filter(member => member.isGroup)
            .forEach((member: MemberDto) => {
                parseGroups(member, groups);
            });
    }
    return groups;
};

const parseInputs = (group: MemberDto, inputs: Array<InputModel | ImageInputModel> = []) => {
    group.members.forEach((member: MemberDto) => {
        if (member.isGroup && !member.isParameter) {
            if (member.properties?.css_pairingNames) {
                inputs = inputs.concat(handlePairingFields(member));
            } else {
                parseInputs(member, inputs);
            }
        } else if (visibleInput(member)) {
            if (member.domain) {
                inputs.push(inputDtoToInputModel(member));
            }
        }
    });
    return inputs;
};

const groupInputsByRow = (inputs: Array<InputModel | ImageInputModel>) => {
    const rows: InputRowModel[] = [];
    const groupedInputs = groupBy(inputs, input => input.rowName);
    Object.keys(groupedInputs).forEach(row => {
        rows.push({
            rowName: row,
            inputs: groupedInputs[row],
        });
    });
    return rows;
};

const visibleInput = (input: MemberDto) => {
    return (
        input.isParameter && !propertyTruthy(input.properties?.css_hide) && !propertyTruthy(input.properties?.hidden)
    );
};

const inputDtoToInputModel = (input: MemberDto) => {
    return {
        name: input.name,
        description: input.description,
        defaultValue: {
            name: !getPlaceholder(input) || input.committed || input.alwaysCommitted ? input.value : "",
            description:
                !getPlaceholder(input) || input.committed || input.alwaysCommitted ? input.valueDescription : "",
        },
        disabled: input.properties?.guitype === "readonly",
        groupName: getGroupName(input),
        rowName: input.properties?.css_rowName,
        tcInfoText: input.properties?.tc_info_text,
        colOffset: Number(input.properties?.css_colOffset) || 0,
        colSpan: input.properties?.guitype === "imagetext_buttons" ? 12 : Number(input.properties?.css_colSpan) || 3,
        values: getValueMaps(input),
        type: getInputType(input),
        src: getImageSrc(input.properties),
        alt: getAltText(input),
        tooltip: input.properties?.tc_field_view_text?.replace(/\\n/g, "\n"),
        committed: input.committed,
        units: input.properties?.tc_unit ? `[${input.properties.tc_unit}]` : undefined,
        min: Number(input.domain?.min),
        max: Number(input.domain?.max),
        required: !!getPlaceholder(input),
        placeholder: getPlaceholder(input),
        value: !getPlaceholder(input) || input.committed || input.alwaysCommitted ? input.valueDescription : "",
    };
};

const getGroupName = (input: MemberDto) => {
    const group = translationGroup?.members.find(
        (member: MemberDto) => member.name === input.properties?.css_navigationLabel,
    ) as MemberDto | undefined;
    return group?.description || "Other";
};

const getPlaceholder = (input: MemberDto) => {
    const group = translationGroup?.members.find(member => member.name === input.properties?.css_inputPlaceholder);
    return group?.description;
};

const getInputType = (field: MemberDto) => {
    let inputType = InputType.TEXT;

    if (
        field.properties.guitype === "dropdown" ||
        field.properties.guitype === "radio" ||
        field.properties.tc_gui_input === "dropdown"
    ) {
        inputType = InputType.SELECT;
    } else if (field.domain.name === "int" || field.domain.name === "float") {
        inputType = InputType.NUMBER;
    } else if (field.properties.tc_guitype === "onoff") {
        inputType = InputType.TOGGLE;
    } else if (field.properties.guitype === "imagetext_buttons") {
        inputType = InputType.IMAGE_SELECT;
    } else if (field.properties.guitype === "selected_image") {
        inputType = InputType.IMAGE;
    } else {
        inputType = InputType.TEXT; // add this line
    }

    return inputType;
};

const handlePairingFields = (group: MemberDto) => {
    const pairingNames = group.properties.css_pairingNames!.split(",");
    const groupedInputs = groupInputsByPairedName(group, pairingNames);
    return groupedInputsToInputModels(groupedInputs);
};

const groupInputsByPairedName = (group: MemberDto, pairingNames: string[]) => {
    const inputDtos = getInputDtos(group);
    const groupedInputs = groupBy(inputDtos, dto => {
        let groupName = dto.name;
        pairingNames?.forEach(suffix => {
            groupName = groupName.replace(suffix, "");
        });
        return groupName;
    });
    return groupedInputs;
};

const groupedInputsToInputModels = (groupedInputs: Record<string, MemberDto[]>) => {
    const inputs: InputModel[] = [];
    Object.keys(groupedInputs).forEach(groupedInput => {
        if (groupedInputs[groupedInput].length === 2) {
            const [labelInput, valueInput] = groupedInputs[groupedInput];
            const pairedInput = Object.assign(valueInput, {});
            pairedInput.description = labelInput.valueDescription;
            inputs.push(inputDtoToInputModel(pairedInput));
        }
    });
    return inputs;
};

const getInputDtos = (group: MemberDto, inputs: MemberDto[] = []) => {
    group.members.forEach((member: MemberDto) => {
        if (member.isGroup && !member.isParameter) {
            getInputDtos(member, inputs);
        } else if (visibleInput(member)) {
            inputs.push(member);
        }
    });
    return inputs;
};

const getValueMaps = (member: MemberDto) => {
    let values: Array<ValueModel | ImageValueModel> = [];
    if (member.properties?.guitype !== "text") {
        values = member.domain.elements?.map(element => {
            return {
                name: element.name,
                description: element.description,
                src: getImageSrc(element.properties),
                alt: getAltText(element),
                state: element.state,
            };
        });
    }
    return values;
};

const getImageSrc = (properties?: PropertiesDto) => {
    if (properties?.tc_component_picture) {
        return `${productConfigUrl}/image?path=${encodeURIComponent(properties?.tc_component_picture ?? "")}`;
    }
};

const getAltText = (input: MemberDto | ElementDto) => {
    if (input.properties?.tc_component_picture) {
        const fullString = input.properties.tc_component_picture;
        const fileNameIndex = fullString.indexOf("file=");
        const extensionIndex = fullString.lastIndexOf(".");
        if (fileNameIndex && extensionIndex) {
            const fileName = fullString.substring(fileNameIndex + 5, extensionIndex);
            return `${input.description} ${fileName}`;
        }
    }
    return "";
};

export const isRFQ = (bom: BomDto[]) => {
    return !!traverse(bom, "subItems").find(element => element.name === "rfqBomHierarchy");
};

export const getRFQReason = (bom: BomDto[]) => {
    const rfqElement = traverse(bom, "subItems").find(element => element.name === "rfqBomHierarchy");
    return rfqElement ? rfqElement.description.replace(/\\n/g, "\n") : "";
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const traverse = <T extends Record<string, any>>(array: T[], childProp: string): T[] => {
    let result: T[] = [];
    array.forEach((a: T) => {
        result.push(a);
        if (a[childProp] && Array.isArray(a[childProp])) {
            result = result.concat(traverse(a[childProp], childProp));
        }
    });
    return result;
};

export const traverseForValue = <T extends Record<string, any>>(
    array: T[] | undefined,
    propName: keyof T,
    propValue: T[keyof T],
): T | undefined => {
    if (array) {
        for (let i = 0; i < array.length; i++) {
            if (array[i][propName] === propValue) {
                return array[i];
            }
            if (array[i].members && Array.isArray(array[i].members)) {
                const child = traverseForValue(array[i].members, propName, propValue);
                if (child) {
                    return child;
                }
            }
        }
    }
};
